import {DataGridPro, GridRowId} from '@mui/x-data-grid-pro'
import {keepPreviousData, useQuery, UseQueryResult} from '@tanstack/react-query'
import dayjs from 'dayjs'
import {envBackHost, envImagePath} from 'GLOBAL/envVariables'
import {axiosWithAuth} from 'helper/axiosWithAuth'
import CustomerDetailInfoMdal from 'pages/common/CustomerDetailInfoMdal'
import PhotoModal from 'pages/common/PhotoModal'
import FlaggedReviewDetailModal from 'pages/customer/component/FlaggedReviewDetailModal'
import {
  customerFlaggedReviewColumns,
  flaggedReviewStatusOptions,
} from 'pages/customer/dataConfig/declearColumns'
import {customerFlaggedReviewFilter} from 'pages/customer/dataConfig/declearFilters'
import {customerFlaggedReviewRows} from 'pages/customer/dataConfig/declearRows'
import {useEffect, useMemo, useState} from 'react'
import {useSelector} from 'react-redux'
import {toast, ToastContainer} from 'react-toastify'
import {getAsCauseList} from 'utils/apiCalling/api'
import {fetchCustomerIssueList} from 'utils/apiCalling/reactQueryApi'
import {validationAlert} from 'utils/CustomAlert/Alert'
import CustomPagination from 'utils/designSytem/CustomPagination'
import useSelectionRemap from 'utils/hooks/useSelectionRemap'
import MeterariumPageLayout from 'utils/designSytem/MeterariumPageLayout'
import {
  customerDetailModalClose,
  customerDetailModalOpen,
  imageDetailModalClose,
  imageDetailModalOpen,
  onRowsSelectionHandler,
  photoModalClose,
  photoModalOpen,
  scrollTop,
} from 'utils/func/justUtils'
import {toolTipText} from 'utils/toolTips/toolTipText'
import {customerFlaggedReviewGroup} from '../dataConfig/declearGroupingModel'
import {customerFlaggedReviewSearchOption} from '../dataConfig/declearSearchOptions'
import {useFlaggedReviewAsMutation} from '../hooks/useFlaggedReviewAsMutation'
import FlaggedReviewExcelModal from 'pages/customer/component/FlaggedReviewExcelModal'
import AsBatchOffcanvas from '../modules/asBatchOffcanvas'

const flaggedReviewQuickFilters = flaggedReviewStatusOptions

const CustomerFlaggedReview = (props: any) => {
  /*********************************************************************
   ** Test Data
   *********************************************************************/

  /*********************************************************************
   * 0. Types
   *********************************************************************/

  /*********************************************************************
   * 1. Init Libs
   *********************************************************************/
  const userInfo = useSelector((state: any) => state.userInfo?.userInfo)
  const officeInfo = useSelector((state: any) => state?.officeInfo)
  const cooperInfo = useSelector((state: any) => state?.cooperInfo)
  /*********************************************************************
   * 2. State settings (useState)
   *********************************************************************/
  const [asReasonData, setAsReasonData] = useState<any>('')
  const [checkData, setCheckData] = useState<any>([])
  const [customerDetailData, setCustomerDetailData] = useState<any>(null)
  const [isZoomOpen, setIsZoomOpen] = useState<boolean>(false)
  const [causeList, setCauseList] = useState<any>(null)
  const [selectedRow, setSelectedRow] = useState<any>({})
  const [selectionModel, setSelectionModel] = useState<GridRowId[]>([])
  // filter: 입력창에 바인딩된 작업용 필터 / appliedFilter: 실제 조회에 쓰는 확정 필터(queryKey)
  const [filter, setFilter] = useState<any>(customerFlaggedReviewFilter())
  const [appliedFilter, setAppliedFilter] = useState<any>(customerFlaggedReviewFilter())
  const [customerDetailOpen, setCustomerDetailOpen] = useState<boolean>(false)
  const [accordionOpen, setAccordionOpen] = useState<boolean>(false)
  const [photoData, setPhotoData] = useState<any>({})
  const [targetRowId, setTargetRowId] = useState<any>(0)
  const [photoModal, setPhotoModal] = useState<boolean>(false)
  const [diameterFilter, setDiameterFilter] = useState<any>([])
  const [deviceDetailData, setDeviceDetailData] = useState<any>(null)
  const [imageDetailOpen, setImageDetailOpen] = useState<boolean>(false)
  const [issueDateError, setIssueDateError] = useState<boolean>(false)
  const [downloadOpen, setDownloadOpen] = useState<boolean>(false)
  // 빠른 기간 버튼 클릭 시 시작일 입력창 플래시 애니메이션 트리거(증가할 때마다 실행)
  const [issueStartDateFlashKey, setIssueStartDateFlashKey] = useState<number>(0)

  /*********************************************************************
   * 3. react-query (확인이 필요한 수용가 목록 조회)
   *********************************************************************/
  const {
    data: issueData,
    isFetching: issueFetching,
    refetch: refetchList,
  }: UseQueryResult<any> = useQuery({
    queryKey: ['GetCustomerIssues', officeInfo?.value, cooperInfo?.value, appliedFilter],
    queryFn: () => fetchCustomerIssueList(officeInfo, cooperInfo, appliedFilter),
    retry: false,
    enabled: !!officeInfo?.value,
    // 페이지 전환 시 이전 페이지 데이터를 유지해 빈 화면 깜빡임을 방지
    placeholderData: keepPreviousData,
    // A/S 팀 배정 등은 다른 페이지에서 일어나 이 목록을 invalidate하지 않는다.
    // 그 페이지에 다녀와 이 페이지로 복귀(mount)하면 항상 재조회해 최신 배정 상태를 반영.
    // (창 포커스/alt-tab 재조회는 불필요해서 켜지 않음)
    refetchOnMount: 'always',
  })

  const rows = useMemo(
    () => customerFlaggedReviewRows(issueData?.customerIssues ?? []),
    [issueData]
  )

  useSelectionRemap({rows, checkData, selectionModel, setCheckData, setSelectionModel})
  // 목록 쿼리의 count는 issueTypes 필터가 반영된 "필터된 총계" → 페이지네이션용
  const totalCount = issueData?.count ?? null
  // isFetching 기준: 이전 데이터를 유지한 채 재조회 중일 때도 로딩 진행바가 표시됨
  const loading = issueFetching

  /*********************************************************************
   * 3-1. 칩 카운트 전용 쿼리
   ** 칩(issueTypes)을 누르면 API가 그 필터 기준으로 issueCounts를 다시 계산해
   ** 칩별 건수/전체가 변질된다. 이를 막기 위해 issueTypes·page를 제외한 문맥으로만
   ** 카운트를 조회한다. → 칩을 눌러도 이 쿼리의 queryKey는 그대로라 재조회되지 않고
   ** 캐시된 "칩과 무관한 전체 분포"가 유지된다.
   *********************************************************************/
  const countsFilter = useMemo(() => {
    const {issueTypes, page, ...rest} = appliedFilter ?? {}
    // 카운트만 필요하므로 행은 최소로 요청(집계는 전체 매칭 대상 기준으로 내려옴)
    return {...rest, page: 1, rowPerPage: 1}
  }, [appliedFilter])

  const {data: countsData}: UseQueryResult<any> = useQuery({
    queryKey: ['GetCustomerIssueCounts', officeInfo?.value, cooperInfo?.value, countsFilter],
    queryFn: () => fetchCustomerIssueList(officeInfo, cooperInfo, countsFilter),
    retry: false,
    enabled: !!officeInfo?.value,
    // 페이지 재진입(mount) 시 재조회. 창 포커스/alt-tab 재조회는 켜지 않음.
    refetchOnMount: 'always',
  })

  // 칩별 건수·전체 건수는 칩 선택과 무관한 카운트 쿼리에서 가져온다
  const issueCounts: Record<string, number> = countsData?.issueCounts ?? {}
  const overallCount = countsData?.count ?? null

  // A/S 신청/취소 mutation (성공 시 목록 자동 무효화 → 재조회)
  const {requestAs, cancelAs, isAsPending} = useFlaggedReviewAsMutation(userInfo)

  /*********************************************************************
   * 4. Handlers (function)
   *********************************************************************/

  const getOfficeDiameter = async () => {
    const res = await axiosWithAuth().get(`${envBackHost}/${officeInfo.value}/mmd`)

    if (res.data.res) {
      const result = res.data.res.map((value: any) => ({
        value,
        name: `${value}mm`,
      }))

      setDiameterFilter(result)
    }
  }

  const openCustomerDetailModal = () => customerDetailModalOpen(setCustomerDetailOpen)
  const closeCustomerDetailModal = () => customerDetailModalClose(setCustomerDetailOpen)
  const closePhotoModal = () => photoModalClose(setPhotoModal)
  const openPhotoModal = () => photoModalOpen(setPhotoModal)
  const openImageDetailModal = () => imageDetailModalOpen(setImageDetailOpen)
  const closeImageDetailModal = () => imageDetailModalClose(setImageDetailOpen)

  const columnGroupingModel = customerFlaggedReviewGroup

  // 조회 필터 확정 → appliedFilter가 바뀌면 useQuery가 자동 재조회
  const applyFilter = (next: any) => {
    setFilter(next)
    setAppliedFilter(next)
  }

  const resetHandler = () => {
    setIssueDateError(false)
    applyFilter(customerFlaggedReviewFilter())
  }

  const searchHandler = () => {
    const {startIssueDate, endIssueDate} = filter
    if (!startIssueDate || !endIssueDate) {
      setIssueDateError(true)
      validationAlert('조회 기간을 선택해주세요.', 'warning')
      return
    }
    if (dayjs(endIssueDate).isAfter(dayjs(startIssueDate).add(1, 'month'), 'day')) {
      setIssueDateError(true)
      validationAlert('조회 기간을 최대 1개월 이내로 설정 부탁드립니다.', 'warning')
      return
    }
    setIssueDateError(false)
    applyFilter({...filter, page: 1})
  }

  // 종료일 기준으로 시작일을 지정 기간(예: 4일/1주일/1개월) 전으로 세팅(값만 변경, 조회는 검색 버튼으로).
  // 종료일이 비어있으면 오늘을 기준으로 함.
  const setIssueDateQuickRange = (amount: number, unit: 'day' | 'week' | 'month') => {
    const end = filter?.endIssueDate ? dayjs(filter.endIssueDate) : dayjs()
    const start = end.subtract(amount, unit)
    setFilter({
      ...filter,
      startIssueDate: start.format('YYYYMMDD'),
      endIssueDate: end.format('YYYYMMDD'),
    })
    // 시작일이 바뀌었음을 시각적으로 알리는 플래시 애니메이션 트리거
    setIssueStartDateFlashKey((k) => k + 1)
  }

  // 칩(이슈 유형)은 누를 때마다 즉시 조회한다.
  // 단, 필터 컴포넌트의 미확정 편집(셀렉트/검색어/날짜)이 딸려 들어가지 않도록
  // 마지막으로 '검색'된 조건(appliedFilter) 위에 issueTypes만 바꿔 적용한다.
  // filter.issueTypes도 동기화해 칩 활성 하이라이트를 맞춘다.
  const toggleQuickFilter = (key: string) => {
    const current: string[] = appliedFilter?.issueTypes ?? []
    const nextTypes = current.includes(key)
      ? current.filter((k: string) => k !== key)
      : [...current, key]
    setAppliedFilter((prev: any) => ({...prev, issueTypes: nextTypes, page: 1}))
    setFilter((prev: any) => ({...prev, issueTypes: nextTypes, page: 1}))
  }

  const clearQuickFilter = () => {
    setAppliedFilter((prev: any) => ({...prev, issueTypes: [], page: 1}))
    setFilter((prev: any) => ({...prev, issueTypes: [], page: 1}))
  }

  /*********************************************************************
   * 5. UseEffect
   *********************************************************************/
  // 페이지네이션(페이지/페이지당 개수)만 즉시 반영한다.
  // 필터 컴포넌트의 셀렉트/검색어/날짜는 '검색' 버튼으로만 적용되도록 여기서 제외.
  // appliedFilter의 나머지(이미 검색으로 확정된) 값은 보존하고 페이지 관련 값만 갱신.
  useEffect(() => {
    setAppliedFilter((prev: any) => ({
      ...prev,
      page: filter?.page,
      rowPerPage: filter?.rowPerPage,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter?.rowPerPage, filter?.page])

  // 페이지 변경 시 목록 상단으로 스크롤 이동
  useEffect(() => {
    scrollTop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter?.page])

  // 사업소/협력사 기준 부가 데이터 로드
  useEffect(() => {
    scrollTop()
    getAsCauseList(setCauseList)
    getOfficeDiameter()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeInfo?.value, cooperInfo?.value])

  // 사업소 변경 시 필터 초기화
  useEffect(() => {
    const def = customerFlaggedReviewFilter()
    setFilter(def)
    setAppliedFilter(def)
  }, [officeInfo?.value])

  // 날짜를 다시 조정하면 에러 표시 해제
  useEffect(() => {
    setIssueDateError(false)
  }, [filter?.startIssueDate, filter?.endIssueDate])

  /*********************************************************************
   * 6. DataGrid Props settings
   *********************************************************************/
  const columns: any = customerFlaggedReviewColumns(
    openCustomerDetailModal,
    setCustomerDetailData,
    userInfo,
    refetchList,
    officeInfo,
    openPhotoModal,
    setPhotoData,
    setTargetRowId,
    setSelectedRow,
    setIsZoomOpen,
    openImageDetailModal,
    setDeviceDetailData,
    requestAs,
    cancelAs,
    isAsPending
  )
  /*********************************************************************
   * 7. Page configuration
   *********************************************************************/
  return (
    <>
      <MeterariumPageLayout
        headerDescription={toolTipText.customerFlaggedReview.description}
        tooltipText={toolTipText.customerFlaggedReview.text}
        hideSearchOptions={accordionOpen}
        setHideSearchOptions={setAccordionOpen}
        filter={filter}
        id={'customer-flagged-review'}
        setFilter={setFilter}
        searchCallback={searchHandler}
        total={totalCount}
        control={[]}
        actions={['download', 'as']}
        searchOptions={[]}
        checkData={checkData}
        selectedCount={selectionModel.length}
        asValue={{
          asReasonData,
          setAsReasonData,
          setSelectionModel,
          setCheckData,
          causeList,
          // A/S 등록·취소 후 갱신은 현재 페이지를 유지한다
          // (searchCallback은 검색용이라 page를 1로 되돌린다)
          refreshCallback: refetchList,
        }}
        resetCallbalck={resetHandler}
        newSearchOptions={customerFlaggedReviewSearchOption}
        newSearchField={true}
        newSearchFieldFilter={{diameter: diameterFilter}}
        onDownloadOpen={() => setDownloadOpen(true)}
        loading={loading}
        hideTotalCount
        compactSearch
        dateFieldsInDefaultSearch
        issueDateError={issueDateError}
        issueStartDateFlashKey={issueStartDateFlashKey}
        issueDateQuickButtons={[
          {
            label: '4일',
            tooltip: '종료일 기준 4일 (동일 이미지 확인용)',
            onClick: () => setIssueDateQuickRange(4, 'day'),
          },
          {
            label: '1주일',
            tooltip: '종료일 기준 1주일',
            onClick: () => setIssueDateQuickRange(1, 'week'),
          },
          {
            label: '1개월',
            tooltip: '종료일 기준 1개월',
            onClick: () => setIssueDateQuickRange(1, 'month'),
          },
        ]}
      >
        <div className='flex flex-wrap items-center gap-2 p-4 mb-5 bg-gray-50 border border-gray-200 rounded-xl'>
          <button
            type='button'
            onClick={clearQuickFilter}
            className={`flex items-center gap-1 px-4 py-3 rounded-full text-base font-semibold shadow-sm transition ${
              !filter.issueTypes?.length
                ? 'bg-gray-900 text-white'
                : 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-100'
            }`}
          >
            전체 <span className='font-normal opacity-70'>{overallCount ?? 0}</span>
          </button>
          {flaggedReviewQuickFilters.map(({key, label, dotClassName}) => {
            const isActive = filter.issueTypes?.includes(key)
            return (
              <button
                key={key}
                type='button'
                onClick={() => toggleQuickFilter(key)}
                className={`flex items-center gap-2 px-4 py-3 rounded-full text-base font-semibold border-2 shadow-sm transition ${
                  isActive
                    ? 'bg-white border-gray-900 text-gray-900'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${dotClassName}`} />
                {label} <span className='text-gray-400 font-normal'>{issueCounts[key] ?? 0}</span>
              </button>
            )
          })}
        </div>

        <div className='border border-gray-200 rounded-xl overflow-hidden'>
          <DataGridPro
            rowHeight={180}
            headerHeight={30}
            autoHeight={true}
            columnGroupingModel={columnGroupingModel}
            experimentalFeatures={{columnGrouping: true}}
            rows={rows}
            rowCount={rows?.length}
            columns={columns}
            disableColumnMenu
            checkboxSelection
            onSelectionModelChange={(ids, selection) => {
              const selectedRowsData = ids.map((id) => rows.find((row: any) => row.id === id))
              onRowsSelectionHandler(ids, [
                [setCheckData, selectedRowsData],
                [setSelectionModel, ids],
              ])
            }}
            selectionModel={selectionModel}
            loading={loading}
            paginationMode={'server'}
            hideFooterPagination={true}
            pagination={true}
            hideFooter={true}
            sx={{
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f9fafb',
              },
              '& .MuiDataGrid-row.row-error': {
                backgroundColor: '#ffe4e6',
              },
              '& .MuiDataGrid-row.row-error:hover': {
                backgroundColor: '#ffe4e6',
              },
            }}
          />
        </div>

        <div className='mt-3'>
          <CustomPagination filter={filter} setFilter={setFilter} totalCount={totalCount} />
        </div>
      </MeterariumPageLayout>

      <CustomerDetailInfoMdal
        data={customerDetailData}
        open={customerDetailOpen}
        onClose={closeCustomerDetailModal}
        getList={refetchList}
      />

      <FlaggedReviewDetailModal
        data={deviceDetailData}
        open={imageDetailOpen}
        onClose={closeImageDetailModal}
        // 상세 진입 시 메인에서 조회한 이슈 기간으로 초기 조회하도록 동기화
        mainDateRange={{
          startDate: appliedFilter?.startIssueDate,
          endDate: appliedFilter?.endIssueDate,
        }}
      />

      <PhotoModal
        setData={setPhotoData}
        data={photoData}
        open={photoModal}
        onClose={closePhotoModal}
        page='info'
        rowCount={targetRowId}
        filter={filter}
        pagenation={filter.page}
        perPage={filter.rowPerPage}
        getList={refetchList}
        count={totalCount}
      />

      <FlaggedReviewExcelModal
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        filter={appliedFilter}
        officeInfo={officeInfo}
        cooperInfo={cooperInfo}
      />

      <AsBatchOffcanvas toast={toast} />
      <ToastContainer />

      {isZoomOpen && selectedRow && (
        <div className='fixed top-[30%] left-[40%] -translate-x-1/2 z-50'>
          <div className='relative bg-black/90 text-white p-4 rounded-lg flex flex-col gap-6'>
            {/* 시작 이미지 */}
            <div className='flex flex-col items-center flex-1'>
              <span className='mb-2 text-base font-semibold'>시작 이미지</span>
              <img
                src={`${envImagePath}${selectedRow.sfilePath}`}
                alt='시작 이미지'
                className='w-auto min-w-[320px] min-h-[300px] max-h-[300px] object-contain rounded border'
                style={{
                  transform: `${
                    selectedRow.filePath &&
                    ((Number(selectedRow.rotate) > 90 && Number(selectedRow.rotate) < 270) ||
                      (Number(selectedRow.rotate) < -90 && Number(selectedRow.rotate) > -270))
                      ? `rotate(180deg)`
                      : 'rotate(0deg)'
                  }`,
                }}
              />
            </div>

            {/* 마지막 이미지 */}
            <div className='flex flex-col items-center flex-1'>
              <span className='mb-2 text-base font-semibold'>마지막 이미지</span>
              <img
                src={`${envImagePath}${selectedRow.efilePath}`}
                alt='마지막 이미지'
                className='w-auto min-w-[320px] min-h-[300px] max-h-[300px] object-contain rounded border'
                style={{
                  transform: `${
                    selectedRow.filePath &&
                    ((Number(selectedRow.rotate) > 90 && Number(selectedRow.rotate) < 270) ||
                      (Number(selectedRow.rotate) < -90 && Number(selectedRow.rotate) > -270))
                      ? `rotate(180deg)`
                      : 'rotate(0deg)'
                  }`,
                }}
              />
            </div>

            {/* 닫기 버튼 */}
            <button
              className='absolute top-2 right-2  text-white rounded-full w-8 h-8 flex items-center justify-center'
              onClick={() => setIsZoomOpen(false)}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  )
}
export default CustomerFlaggedReview
